import { LocaleImage, LocaleAnchor } from '@/i18n/router';
import { useLocale, useMessages } from '@/i18n/LocaleProvider';
import messages from '@/i18n/catalogs/corporate/ProductsSection';
import React from 'react';

import { Link } from '@/i18n/router';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import SectionHeading from '@/components/SectionHeading';
import {
  Zap,
  Shield,
  Database,
  ArrowRight,
  Server,
  Globe,
  Cpu,
  TrendingDown,
  Clock,
  Lock,
  Cloud,
  Smartphone,
  Bot,
  Download
} from 'lucide-react';
import { CLOUD_SIGNUP_URL } from '@/data/cloud';
import { hardchargerArt } from '@/data/hardcharger';
import { tpchCopy } from '@/data/hardcharger-launch';
import { useHardchargerVersions } from '@/hooks/useHardchargerVersions';
import gizmoEdgeIcon from '@/assets/gizmoedge-logo-icon.png';
import AnimatedSection from './AnimatedSection';
import TpcDisclaimer from '@/components/TpcDisclaimer';

const ProductsSection = () => {
  const m = useMessages(messages);
  const tpch = tpchCopy(useLocale().locale);
  // The startup banner of the latest release (the build's versions until it is read).
  const versions = useHardchargerVersions();


  return (
    <section id="products" className="scroll-mt-24 relative">
      {/* Products Header */}
      <div className="bg-background py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center">
              <SectionHeading anchor="products" className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">{m.s1}</SectionHeading>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{m.s2}</p>
            </div>
          </AnimatedSection>
        </div>
      </div>

      {/* Hardcharger: the lead product (GizmoSQL's successor) */}
      <div className="relative hc-band text-white overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-hc-azure/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-hc-scarlet/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="slide-left">
              <div className="space-y-6">
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30 transition-colors">{m.s3}</Badge>
                  <Badge className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-100 border-amber-400/30 hover:from-amber-500/30 hover:to-orange-500/30 transition-colors">{m.s4}</Badge>
                  <Badge className="bg-gradient-to-r from-gizmo-500/20 to-gizmo-500/20 text-gizmo-100 border-gizmo-400/30 hover:from-gizmo-500/30 hover:to-gizmo-500/30 transition-colors">{m.s5}</Badge>
                </div>
                <div className="flex items-center gap-4">
                  <LocaleImage
                    src={hardchargerArt.head}
                    alt={m.s6}
                    width={128}
                    height={128}
                    loading="lazy"
                    className="h-16 w-16 object-contain"
                  />
                  <SectionHeading withId anchor="hardcharger" as="h2" className="text-4xl md:text-5xl font-bold">Hardcharger™</SectionHeading>
                </div>
                <p className="text-xl text-gray-300 leading-relaxed">{m.s7}</p>

                {/* Feature highlights */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                  <div className="flex items-start gap-3 group">
                    <div className="p-2 bg-white/10 rounded-lg group-hover:bg-white/20 transition-colors">
                      <Zap className="h-5 w-5 text-hc-gold" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{m.s8}</h3>
                      <p className="text-sm text-gray-400">{m.s9}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 group">
                    <div className="p-2 bg-white/10 rounded-lg group-hover:bg-white/20 transition-colors">
                      <Globe className="h-5 w-5 text-hc-azure" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{m.s10}</h3>
                      <p className="text-sm text-gray-400">{m.s11}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 group">
                    <div className="p-2 bg-white/10 rounded-lg group-hover:bg-white/20 transition-colors">
                      <Lock className="h-5 w-5 text-hc-azure" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{m.s12}</h3>
                      <p className="text-sm text-gray-400">{m.s13}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 group">
                    <div className="p-2 bg-white/10 rounded-lg group-hover:bg-white/20 transition-colors">
                      <Server className="h-5 w-5 text-hc-gold" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{m.s14}</h3>
                      <p className="text-sm text-gray-400">{m.s15}</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 pt-4">
                  <Link to="/hardcharger/install">
                    <Button
                      size="lg"
                      className="bg-white text-hc-navy hover:bg-hc-paper dark:bg-white dark:text-hc-navy dark:hover:bg-hc-paper font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                      data-ga-cta="install_hardcharger"
                      data-ga-location="home_products"
                    >
                      <Download className="mr-2 h-5 w-5" />{m.s16}</Button>
                  </Link>
                  <Link to="/hardcharger">
                    <Button
                      size="lg"
                      variant="outline"
                      className="bg-transparent border-white text-white hover:bg-white/10 transition-all dark:bg-transparent dark:border-white dark:text-white dark:hover:bg-white/10"
                      data-ga-cta="explore_hardcharger"
                      data-ga-location="home_products"
                    >{m.s17}<ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <LocaleAnchor href={CLOUD_SIGNUP_URL}>
                    <Button
                      size="lg"
                      variant="outline"
                      className="bg-transparent border-white text-white hover:bg-white/10 transition-all dark:bg-transparent dark:border-white dark:text-white dark:hover:bg-white/10"
                      data-ga-cta="cloud_signup"
                      data-ga-location="home_products"
                    ><Cloud className="mr-2 h-5 w-5" />{m.s18}</Button>
                  </LocaleAnchor>
                </div>
                {/* Old deep links to /#gizmosql land here. */}
                <p id="gizmosql" className="scroll-mt-24 text-sm text-gray-300">
                  {m.s25}
                  <Link to="/gizmosql" className="underline underline-offset-4 hover:text-white" data-ga-cta="explore_gizmosql" data-ga-location="home_products">{m.s27}</Link>
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="slide-right">
              <div className="relative">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-hc-cobalt/25 to-hc-azure/10 rounded-2xl blur-2xl transform scale-105" />

                {/* Terminal card */}
                <div className="relative bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-2xl overflow-hidden">
                  <div className="flex items-center px-4 py-3 border-b border-white/10 bg-black/20">
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 rounded-full bg-red-400" />
                      <div className="w-3 h-3 rounded-full bg-yellow-400" />
                      <div className="w-3 h-3 rounded-full bg-green-400" />
                    </div>
                    <span className="ml-4 text-sm text-white/60 font-mono">{m.s19}</span>
                  </div>
                  <div className="p-6 font-mono text-sm">
                    <div className="text-hc-azure">{m.s20}</div>
                    <div className="mt-4 space-y-1 whitespace-pre text-green-400">
                      <p data-testid="hardcharger-banner">{`Hardcharger ${versions.hardcharger} | Chargewire ${versions.chargewire} | DuckDB ${versions.duckdb}`}</p>
                      <p>{m.s22}</p>
                      <p>{m.s23}</p>
                      <p className="text-hc-gold animate-pulse">✅ {m.s24}</p>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-white/60">{m.s26}</p>
                      <p className="text-2xl font-bold text-white">{tpch.hcSeconds} s <span className="text-sm font-normal text-green-400">({tpch.hcCostRounded})</span></p>
                      <Link to="/hardcharger#performance" className="mt-1 inline-block text-xs text-white/60 underline underline-offset-2 hover:text-white" data-ga-cta="view_benchmarks" data-ga-location="home_products">{m.s28}</Link>
                    </div>
                  </div>
                </div>
                <TpcDisclaimer className="relative mt-3 text-white/60" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>

    </section>
  );
};

export default ProductsSection;
